'use client';

import EmptyChatMessageInput from '@/components/SearchInput/EmptyChatMessageInput';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateChat } from '@/hooks/useCreateChat';
import { useLanguage, useTranslation } from '@/lib/i18n';
import { useHeader } from '@/contexts/HeaderContext';
import GradientText from '@/components/GradientText';
import { useAsyncEffect, useInViewport } from 'ahooks';
import { useRightDrawer } from '@/contexts/DrawerContext';
import MarketPulse from './MarketPulse';
import useSWR from 'swr';
import ChatStatusModal from './ChatStatusModal';
import { useTrackingWalletsApi } from '../hooks/use-tracking-wallets';
import { ChartLine } from 'lucide-react';
import { logEvent } from '@/lib/firebase';
import { trackXAdsEvent } from '@/lib/xAds';

export default function PredicQuery() {
  const mainInputRef = useRef<HTMLDivElement>(null);

  const { setIsTransparent } = useHeader();
  const { t } = useTranslation();
  const { createChat, isCreating } = useCreateChat();
  const { openDrawer, setDrawerWidth } = useRightDrawer();

  const callback = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.intersectionRatio < 1) {
        setIsTransparent(false);
      } else {
        setIsTransparent(true);
      }
    },
    [setIsTransparent],
  );

  const [inViewport] = useInViewport(mainInputRef, {
    // rootMargin: '-60px',
    threshold: [0, 0.25, 0.5, 0.75, 1],
    callback,
  });

  useEffect(() => {
    setDrawerWidth(400);

    openDrawer(<MarketPulse />, t('Events'), true);
  }, [openDrawer, setDrawerWidth, t]);

  return (
    <div className="flex flex-col items-center">
      <div
        className="w-full pt-15 -mt-15"
        ref={mainInputRef}
        style={{ background: 'url(/image/main-bg.png) no-repeat right top' }}
      >
        <Slogan />
        <Operations />
        <div className="w-full mt-3">
          <EmptyChatMessageInput
            loading={false}
            floating={false}
            sendMessage={(message: string) => createChat({ message })}
            isCreating={isCreating}
            showProductIcon={false}
            colorfulInput={false}
          />
        </div>
      </div>

      {/* 悬浮输入框 */}
      {!inViewport && (
        <div className="fixed bottom-6 transform z-10 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div
            className="max-w-190 mx-auto shadow-2xl rounded-[20px]"
            style={{ width: mainInputRef.current?.clientWidth }}
          >
            <EmptyChatMessageInput
              loading={false}
              floating
              sendMessage={(message: string) => createChat({ message })}
              isCreating={isCreating}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Operations() {
  const { createChat, isCreating } = useCreateChat();
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const { t } = useTranslation();

  const [questions, setQuestions] = useState<string[]>([]);

  const { data, error } = useTrackingWalletsApi({
    enabled: !!apiAccessToken,
    swr: { revalidateOnFocus: false },
  });

  const handleQuery = (msg: string) => {
    logEvent('click_pre_question', {
      context_type: '',
      context_id: '',
      question_length: msg.length,
    });
    trackXAdsEvent('click_pre_question', {
      question_length: msg.length,
    });
    createChat({ message: msg });
  };

  useAsyncEffect(async () => {
    const fetcher = async (url: string) => {
      const resp = await fetch(url);
      if (resp.ok) {
        const list = await resp.json();
        return list.length > 0;
      }
      return false;
    };
    const reqs = data?.data?.map((item) =>
      fetcher(
        `https://data-api.polymarket.com/positions?sizeThreshold=1&limit=100&sortBy=CURRENT&sortDirection=DESC&user=${item.address}&redeemable=false`,
      ),
    );

    if (reqs) {
      const result = await Promise.all(reqs);
      setQuestions(
        [
          'What markets should I check today?',
          'What are the top events today?',
          result.includes(true) ? "Today's portfolio suggestions" : '',
        ].filter(Boolean),
      );
    }
  }, [data]);

  return (
    <>
      <div className="flex items-center gap-3 text-sm">
        {questions.map((item) => (
          <div
            key={item}
            title={t(item)}
            className="h-9.5 grow cursor-pointer hover:text-[#b7b9bc] leading-9.5 rounded-full flex items-center gap-1 border border-white/20 px-4"
            onClick={() => handleQuery(t(item))}
          >
            <ChartLine size={16} stroke="#FF6705" />
            <span className="flex-1 line-clamp-1">{t(item)}</span>
          </div>
        ))}
      </div>
      {isCreating && <ChatStatusModal />}
    </>
  );
}

function Slogan() {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  if (currentLanguage.code === 'en') {
    return (
      <div className="">
        <h1 className="text-[42px] leading-12.5 font-semibold!">Decode the Odds Before the</h1>
        <GradientText
          className="text-[42px] leading-12.5 font-semibold! mb-10! ml-0!"
          colors={['#4390FC', '#6BFBFF', '#934AF8']}
          animationSpeed={5}
        >
          Market Moves
        </GradientText>
      </div>
    );
  }

  return (
    <div className="">
      <h1 className="text-[42px] leading-12.5 font-semibold!"></h1>
      <GradientText
        className="text-[42px] leading-12.5 font-semibold! mb-10! ml-0!"
        colors={['#4390FC', '#6BFBFF', '#934AF8']}
        animationSpeed={5}
      >
        {t('Decode the Odds Before the Market Moves')}
      </GradientText>
    </div>
  );
}
